<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { fetchWorkDetail } from '@/services/api'

// Fallback images
import coverImg from '@/assets/img/photos/movie.jpg'
import bts1 from '@/assets/img/photos/g8.jpg'
import bts2 from '@/assets/img/photos/g9.jpg'
import bts3 from '@/assets/img/photos/g10.jpg'
import bgSmile from '@/assets/img/bg-smilepicture.png'
import SomeOfWorks from '../components/SomeOfWorks.vue'

const route = useRoute()
const workData = ref(null)
const isLoading = ref(true)
const isPlaying = ref(false)

// Lightbox state
const selectedGallery = ref(null)
const showLightbox = ref(false)

// Open lightbox with gallery item
const openLightbox = (gallery) => {
    selectedGallery.value = gallery
    showLightbox.value = true
    document.body.style.overflow = 'hidden'
}

// Close lightbox
const closeLightbox = () => {
    showLightbox.value = false
    selectedGallery.value = null
    document.body.style.overflow = ''
}

// Navigate to previous image
const prevImage = () => {
    if (!workData.value?.galleries) return
    const currentIndex = workData.value.galleries.findIndex(g => g.id === selectedGallery.value?.id)
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : workData.value.galleries.length - 1
    selectedGallery.value = workData.value.galleries[prevIndex]
}

// Navigate to next image
const nextImage = () => {
    if (!workData.value?.galleries) return
    const currentIndex = workData.value.galleries.findIndex(g => g.id === selectedGallery.value?.id)
    const nextIndex = currentIndex < workData.value.galleries.length - 1 ? currentIndex + 1 : 0
    selectedGallery.value = workData.value.galleries[nextIndex]
}

// Handle keyboard navigation
const handleKeydown = (e) => {
    if (!showLightbox.value) return
    if (e.key === 'Escape') closeLightbox()
    if (e.key === 'ArrowLeft') prevImage()
    if (e.key === 'ArrowRight') nextImage()
}

// Add keyboard listener on mount
onMounted(async () => {
    window.addEventListener('keydown', handleKeydown)

    try {
        const data = await fetchWorkDetail(slug.value)
        workData.value = data
    } catch (error) {
        console.error('Failed to load work detail:', error)
    } finally {
        isLoading.value = false
    }
})

// Cleanup keyboard listener on unmount
onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
    document.body.style.overflow = ''
})

// Get the slug from route params
const slug = computed(() => route.params.slug)

// Detect video type and extract IDs
const videoType = computed(() => {
    const url = workData.value?.videoUrl
    if (!url) return 'direct'

    if (url.includes('youtube.com') || url.includes('youtu.be')) {
        return 'youtube'
    }
    if (url.includes('drive.google.com')) {
        return 'gdrive'
    }
    if (url.includes('onedrive.live.com') || url.includes('1drv.ms') || url.includes('sharepoint.com')) {
        return 'onedrive'
    }
    return 'direct'
})

// Extract YouTube video ID
const youtubeId = computed(() => {
    const url = workData.value?.videoUrl
    if (!url) return ''

    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    const match = url.match(regex)
    return match ? match[1] : ''
})

// YouTube embed URL with autoplay
const youtubeEmbedUrl = computed(() => {
    return `https://www.youtube.com/embed/${youtubeId.value}?rel=0&modestbranding=1&autoplay=1`
})

// Convert Google Drive share link to embed link
const gdriveEmbedUrl = computed(() => {
    const url = workData.value?.videoUrl
    if (!url) return ''

    const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/)
    if (match) {
        return `https://drive.google.com/file/d/${match[1]}/preview`
    }
    return ''
})

// Convert OneDrive share link to embed link
const onedriveEmbedUrl = computed(() => {
    const url = workData.value?.videoUrl
    if (!url) return ''

    // Extract file ID from OneDrive share link
    // Format 1: https://onedrive.live.com/?resid=[ID]&authkey=[KEY]
    // Format 2: https://1drv.ms/v/s![ID]
    // Format 3: https://smilepicture-my.sharepoint.com/:v:/g/personal/[path]/[ID]
    let embedUrl = url
    
    if (url.includes('sharepoint.com')) {
        const match = url.match(/\/([a-zA-Z0-9_-]+)$/)
        if (match) {
            const fileId = match[1]
            embedUrl = url;
        }
    } else if (url.includes('onedrive.live.com')) {
        embedUrl = url.replace('guestinteractiontime=0&', '')
        embedUrl = embedUrl + (embedUrl.includes('?') ? '&' : '?') + 'em=2'
    } else if (url.includes('1drv.ms')) {
        const match = url.match(/s!([A-Za-z0-9_-]+)/)
        if (match) {
            embedUrl = `https://onedrive.live.com/embed?resid=${match[1]}&authkey=!AEzOfeSvbKwYrmQ&em=2`
        }
    }
    
    return embedUrl
})

// Play video function
const playVideo = () => {
    isPlaying.value = true
}

// Poster image
const poster = computed(() => workData.value?.image || coverImg)

// Share links
const shareLinks = computed(() => {
    const url = encodeURIComponent(window.location.href)
    const title = encodeURIComponent(workData.value?.title || 'SmilePictures Work')
    return {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
        whatsapp: `https://wa.me/?text=${title}%20-%20${url}`,
        telegram: `https://t.me/share/url?url=${url}&text=${title}`
    }
})

const copyShareLink = async () => {
    await navigator.clipboard.writeText(window.location.href)
    alert('Link copied!')
}

// Strip HTML tags from SEO description
const stripHtml = (html) => {
    if (!html) return ''
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
}

// Update SEO meta tags
const updateMetaTags = () => {
    if (!workData.value) return

    const seoTitle = workData.value.seo_title || workData.value.title
    const seoDescription = stripHtml(workData.value.seo_description || workData.value.description)
    const seoKeywords = workData.value.seo_keywords || ''
    const imageUrl = workData.value.image

    // Update document title
    document.title = `${seoTitle} — SmilePictures`

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
        metaDescription = document.createElement('meta')
        metaDescription.name = 'description'
        document.head.appendChild(metaDescription)
    }
    metaDescription.content = seoDescription

    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]')
    if (!metaKeywords) {
        metaKeywords = document.createElement('meta')
        metaKeywords.name = 'keywords'
        document.head.appendChild(metaKeywords)
    }
    metaKeywords.content = seoKeywords

    // Update or create OG title
    let ogTitle = document.querySelector('meta[property="og:title"]')
    if (!ogTitle) {
        ogTitle = document.createElement('meta')
        ogTitle.setAttribute('property', 'og:title')
        document.head.appendChild(ogTitle)
    }
    ogTitle.content = seoTitle

    // Update or create OG description
    let ogDescription = document.querySelector('meta[property="og:description"]')
    if (!ogDescription) {
        ogDescription = document.createElement('meta')
        ogDescription.setAttribute('property', 'og:description')
        document.head.appendChild(ogDescription)
    }
    ogDescription.content = seoDescription

    // Update or create OG image
    let ogImage = document.querySelector('meta[property="og:image"]')
    if (!ogImage) {
        ogImage = document.createElement('meta')
        ogImage.setAttribute('property', 'og:image')
        document.head.appendChild(ogImage)
    }
    ogImage.content = imageUrl

    // Update or create OG URL
    let ogUrl = document.querySelector('meta[property="og:url"]')
    if (!ogUrl) {
        ogUrl = document.createElement('meta')
        ogUrl.setAttribute('property', 'og:url')
        document.head.appendChild(ogUrl)
    }
    ogUrl.content = window.location.href

    // Update or create Twitter card
    let twitterCard = document.querySelector('meta[name="twitter:card"]')
    if (!twitterCard) {
        twitterCard = document.createElement('meta')
        twitterCard.name = 'twitter:card'
        document.head.appendChild(twitterCard)
    }
    twitterCard.content = 'summary_large_image'

    // Update or create Twitter title
    let twitterTitle = document.querySelector('meta[name="twitter:title"]')
    if (!twitterTitle) {
        twitterTitle = document.createElement('meta')
        twitterTitle.name = 'twitter:title'
        document.head.appendChild(twitterTitle)
    }
    twitterTitle.content = seoTitle

    // Update or create Twitter description
    let twitterDescription = document.querySelector('meta[name="twitter:description"]')
    if (!twitterDescription) {
        twitterDescription = document.createElement('meta')
        twitterDescription.name = 'twitter:description'
        document.head.appendChild(twitterDescription)
    }
    twitterDescription.content = seoDescription

    // Update or create Twitter image
    let twitterImage = document.querySelector('meta[name="twitter:image"]')
    if (!twitterImage) {
        twitterImage = document.createElement('meta')
        twitterImage.name = 'twitter:image'
        document.head.appendChild(twitterImage)
    }
    twitterImage.content = imageUrl
}

// Clear/reset meta tags to defaults
const resetMetaTags = () => {
    document.title = 'Work Detail — SmilePictures'

    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) metaDescription.content = ''

    const metaKeywords = document.querySelector('meta[name="keywords"]')
    if (metaKeywords) metaKeywords.content = ''

    const ogTitle = document.querySelector('meta[property="og:title"]')
    if (ogTitle) ogTitle.content = 'SmilePictures'

    const ogDescription = document.querySelector('meta[property="og:description"]')
    if (ogDescription) ogDescription.content = ''

    const ogImage = document.querySelector('meta[property="og:image"]')
    if (ogImage) ogImage.content = ''

    const ogUrl = document.querySelector('meta[property="og:url"]')
    if (ogUrl) ogUrl.content = ''
}

// Watch for workData changes and update meta tags
watch(workData, (newData) => {
    if (newData) {
        updateMetaTags()
    }
}, { immediate: true })

// Reset meta tags on unmount
const originalOnUnmounted = onUnmounted
onUnmounted(() => {
    resetMetaTags()
})

</script>

<template>
    <!-- Loading State -->
    <div v-if="isLoading" class="wrapper bg-black"
        style="min-height: 100vh; display: flex; align-items: center; justify-content: center;">
        <div class="text-white">
            <h2>Loading...</h2>
        </div>
    </div>

    <template v-else-if="workData">
        <!-- ===== COVER / HERO ===== -->
        <section class="wrapper bg-black">
            <div class="container pt-10 text-center">
                <div class="video-wrapper-detail w-100" style="margin: 0 auto;">
                    <!-- YouTube Player -->
                    <div v-if="videoType === 'youtube'" class="player-detail youtube-player-detail">
                        <iframe v-if="isPlaying" :src="youtubeEmbedUrl" frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen></iframe>
                        <div v-else class="poster-overlay-detail" @click="playVideo">
                            <img :src="poster" alt="Video poster" class="poster-image-detail">
                            <div class="play-button">
                                <svg viewBox="0 0 68 48" width="68" height="48">
                                    <path
                                        d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                                        fill-opacity="0.8"></path>
                                    <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!-- Google Drive Player -->
                    <div v-else-if="videoType === 'gdrive'" class="player-detail gdrive-player-detail">
                        <iframe v-if="isPlaying" :src="gdriveEmbedUrl" frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen></iframe>
                        <div v-else class="poster-overlay-detail" @click="playVideo">
                            <img :src="poster" alt="Video poster" class="poster-image-detail">
                            <div class="play-button">
                                <svg viewBox="0 0 68 48" width="68" height="48">
                                    <path
                                        d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                                        fill-opacity="0.8"></path>
                                    <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!-- OneDrive Player -->
                    <div v-else-if="videoType === 'onedrive'" class="player-detail onedrive-player-detail">
                        <iframe v-if="isPlaying" :src="onedriveEmbedUrl" frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen></iframe>
                        <div v-else class="poster-overlay-detail" @click="playVideo">
                            <img :src="poster" alt="Video poster" class="poster-image-detail">
                            <div class="play-button">
                                <svg viewBox="0 0 68 48" width="68" height="48">
                                    <path
                                        d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                                        fill-opacity="0.8"></path>
                                    <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!-- Direct Video Player -->
                    <video v-else :poster="poster" class="w-100 rounded-3" style="max-width:900px" playsinline controls>
                        <source :src="workData.videoUrl" type="video/mp4" />
                    </video>
                </div>
            </div>
        </section>

        <!-- ===== CONTENT ===== -->
        <section class="wrapper bg-black text-white">
            <div class="container pt-12 pb-10">
                <div class="row g-5">

                    <!-- LEFT CONTENT (75%) -->
                    <div class="col-lg-9">
                        <div class="work-content">
                            <h1 class="display-3 mb-4 text-secondary">
                                {{ workData.title }}
                            </h1>

                            <p class="lead mb-5" style="text-align: justify;" v-html="workData.description"></p>

                            <router-link :to="{ name: 'Services' }" class="learn-more-link">
                                Learn More <i class="uil uil-arrow-up-right"></i>
                            </router-link>
                        </div>
                    </div>

                    <!-- RIGHT ASIDE (25%) -->
                    <aside class="col-lg-3">
                        <div class="work-aside">
                            <div v-if="workData.service" class="aside-group has-divider">
                                <span class="label">Service</span>
                                <span class="value">{{ workData.service.title }}</span>
                            </div>
                            <div v-if="workData.clients && workData.clients.length > 0" class="aside-group has-divider">
                                <span class="label">Client</span>
                                <span class="value">{{workData.clients.map(c => c.name).join(', ')}}</span>
                            </div>

                            <div v-if="workData.categories && workData.categories.length > 0"
                                class="aside-group has-divider">
                                <span class="label">Category Service</span>
                                <span class="value">{{workData.categories.map(c => c.name || c.title).join(', ')
                                    }}</span>
                            </div>

                            <div v-if="workData.industries && workData.industries.length > 0"
                                class="aside-group has-divider">
                                <span class="label">Industry</span>
                                <span class="value">{{workData.industries.map(i => i.name || i.title).join(', ')
                                    }}</span>
                            </div>

                            <div v-if="workData.year" class="aside-group has-divider">
                                <span class="label">Posted</span>
                                <span class="value">{{ workData.month }} {{ workData.year }}</span>
                            </div>

                            <!-- SHARE -->
                            <div class="aside-group has-divider mt-4">
                                <span class="label mb-2 d-block">Share</span>
                                <div class="d-flex gap-3 flex-wrap">
                                    <!-- Facebook -->
                                    <a :href="shareLinks.facebook" target="_blank" rel="noopener" class="share-icon"
                                        aria-label="Share on Facebook">
                                        <i class="uil uil-facebook-f"></i>
                                    </a>

                                    <!-- Twitter / X -->
                                    <a :href="shareLinks.twitter" target="_blank" rel="noopener" class="share-icon"
                                        aria-label="Share on Twitter">
                                        <i class="uil uil-twitter"></i>
                                    </a>

                                    <!-- LinkedIn -->
                                    <a :href="shareLinks.linkedin" target="_blank" rel="noopener" class="share-icon"
                                        aria-label="Share on LinkedIn">
                                        <i class="uil uil-linkedin"></i>
                                    </a>

                                    <!-- WhatsApp -->
                                    <a :href="shareLinks.whatsapp" target="_blank" rel="noopener" class="share-icon"
                                        aria-label="Share on WhatsApp">
                                        <i class="uil uil-whatsapp"></i>
                                    </a>

                                    <!-- Telegram -->
                                    <a :href="shareLinks.telegram" target="_blank" rel="noopener" class="share-icon"
                                        aria-label="Share on Telegram">
                                        <i class="uil uil-telegram"></i>
                                    </a>

                                    <!-- Copy Link -->
                                    <button type="button" class="share-icon btn-copy" aria-label="Copy link"
                                        @click="copyShareLink">
                                        <i class="uil uil-link"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </section>

        <!-- ===== BEHIND THE SCENE ===== -->
        <section v-if="workData.behindTheScenes && workData.behindTheScenes.length > 0" class="wrapper px-0 bg-black"
            :style="{
                backgroundImage: `url(${bgSmile})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }">
            <div class="container pt-14 pb-14">
                <div class="text-center mb-8">
                    <h2 class="display-2 text-white">Behind The Scene</h2>
                </div>

                <!-- BTS GRID -->
                <div class="bts-parent">
                    <div v-for="(bts, index) in workData.behindTheScenes.slice(0, 6)" :key="bts.id || index"
                        :class="`bts-item div${index + 1}`">
                        <img v-if="bts.type === 'image' || !bts.type" :src="bts.url"
                            :alt="bts.title || 'Behind the scene'">
                        <video v-else controls muted>
                            <source :src="bts.url" type="video/mp4" />
                        </video>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== GALLERY ===== -->
        <section v-if="workData.galleries && workData.galleries.length > 0" class="wrapper bg-black">
            <div class="container pt-14 pb-14">
                <div class="text-center mb-8">
                    <h2 class="display-2 text-white">Gallery</h2>
                </div>

                <!-- GALLERY GRID -->
                <div class="gallery-grid">
                    <div v-for="gallery in workData.galleries" :key="gallery.id" class="gallery-item"
                        @click="openLightbox(gallery)">
                        <img :src="gallery.image" :alt="gallery.title || workData.title" class="gallery-image">
                        <div class="gallery-overlay">
                            <i class="uil uil-search-plus"></i>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== LIGHTBOX MODAL ===== -->
        <transition name="lightbox-fade">
            <div v-if="showLightbox && selectedGallery" class="lightbox-modal" @click.self="closeLightbox">
                <!-- Close Button -->
                <button class="lightbox-close" @click="closeLightbox" aria-label="Close lightbox">
                    <i class="uil uil-multiply"></i>
                </button>

                <!-- Navigation Arrows -->
                <button v-if="workData.galleries.length > 1" class="lightbox-nav lightbox-prev" @click="prevImage"
                    aria-label="Previous image">
                    <i class="uil uil-angle-left"></i>
                </button>
                <button v-if="workData.galleries.length > 1" class="lightbox-nav lightbox-next" @click="nextImage"
                    aria-label="Next image">
                    <i class="uil uil-angle-right"></i>
                </button>

                <!-- Image Container -->
                <div class="lightbox-content">
                    <img :src="selectedGallery.image" :alt="selectedGallery.title || workData.title"
                        class="lightbox-image">
                    <div class="lightbox-caption">
                        {{ selectedGallery.title || workData.title }}
                    </div>
                </div>

                <!-- Image Counter -->
                <div v-if="workData.galleries.length > 1" class="lightbox-counter">
                    {{workData.galleries.findIndex(g => g.id === selectedGallery.id) + 1}} /
                    {{ workData.galleries.length }}
                </div>
            </div>
        </transition>

        <SomeOfWorks v-if="!isLoading" :works="workData.recommendedWorks" />
    </template>

    <!-- Error State -->
    <div v-else class="wrapper bg-black"
        style="min-height: 100vh; display: flex; align-items: center; justify-content: center;">
        <div class="text-white text-center">
            <h2>Work Not Found</h2>
            <router-link :to="{ name: 'Our Works' }" class="btn btn-secondary rounded-pill mt-3" style="color: #820358">
                Back to Works
            </router-link>
        </div>
    </div>
</template>


<style scoped>
/* VIDEO DETAIL WRAPPER */
.video-wrapper-detail {
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
}

.player-detail {
    width: 100%;
    height: 100%;
    border-radius: 12px;
}

/* HTML5 Video Player */
.player-detail:where(video) {
    object-fit: cover;
}

/* Iframe Players (YouTube, Google Drive) */
.player-detail:where(.youtube-player-detail, .gdrive-player-detail) {
    position: relative;
}

.youtube-player-detail iframe,
.gdrive-player-detail iframe {
    width: 100%;
    height: 100%;
    border-radius: 12px;
}

.onedrive-player-detail iframe {
    width: 100%;
    height: 100%;
    border-radius: 12px;
}

/* Poster Overlay Detail */
.poster-overlay-detail {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    background: #000;
}

.poster-image-detail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.8;
    transition: opacity 0.3s ease;
}

.poster-overlay-detail:hover .poster-image-detail {
    opacity: 0.6;
}

.play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 68px;
    height: 48px;
    transition: transform 0.2s ease;
}

.poster-overlay-detail:hover .play-button {
    transform: translate(-50%, -50%) scale(1.1);
}

.play-button svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

/* CONTENT LEFT */
.work-content {
    max-width: 720px;
}

.work-eyebrow {
    display: inline-block;
    margin-bottom: 14px;
    font-size: 0.75rem;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #ffae0b;
    opacity: 0.85;
}

/* ASIDE */
.work-aside {
    position: relative;
    padding: 28px 24px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.035);
    border: 1px solid rgba(255, 255, 255, 0.08);
}

/* Biar aside keliatan intentional */
@media (min-width: 992px) {
    .work-aside {
        margin-top: 32px;
    }
}

.aside-group {
    padding-bottom: 14px;
    margin-bottom: 18px;
}

.aside-group.has-divider {
    margin-bottom: 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/* jarak khusus sebelum SHARE */
.aside-group.mt-4 {
    padding-bottom: 0;
    border-bottom: none;
}


.label {
    display: block;
    font-size: 0.7rem;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    opacity: 0.55;
}

.value {
    display: block;
    margin-top: 2px;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.4;
}

/* SHARE ICON */
.share-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid rgba(255, 174, 11, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffae0b;
    transition: all 0.25s ease;
    background: transparent;
    padding: 0;
}

.share-icon:hover {
    background: #ffae0b;
    color: #000;
}

button.share-icon {
    appearance: none;
    -webkit-appearance: none;
    border: 1px solid rgba(255, 174, 11, 0.4);
}

.share-icon i {
    font-size: 1.1rem;
    line-height: 1;
}

/* BTS */
.bts-parent {
    display: grid;
    gap: 12px;
}

.bts-item {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    background: #000;
}

.bts-item img,
.bts-item video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.div1 {
    grid-column: span 2;
    grid-row: span 2;
}

.div2 {
    grid-column-start: 3;
}

.div3 {
    grid-column-start: 3;
    grid-row-start: 2;
}

.div4 {
    grid-row-start: 3;
}

.div5 {
    grid-row-start: 3;
}

.div6 {
    grid-row-start: 3;
}

.learn-more-link {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    color: #ffae0b;
    text-decoration: none;
    padding-bottom: 2px;
}

.learn-more-link i {
    font-size: 1rem;
    transition: transform 0.25s ease;
}

.learn-more-link::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    height: 2px;
    background-color: #ffae0b;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
}

.learn-more-link:hover::after {
    transform: scaleX(1);
}

/* GALLERY GRID */
.gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
}

@media (min-width: 768px) {
    .gallery-grid {
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
        gap: 24px;
    }
}

@media (min-width: 1200px) {
    .gallery-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

.gallery-item {
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    background: #000;
    aspect-ratio: 16 / 9;
    cursor: pointer;
}

.gallery-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.gallery-item:hover .gallery-image {
    transform: scale(1.05);
}

/* GALLERY OVERLAY */
.gallery-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.gallery-item:hover .gallery-overlay {
    opacity: 1;
}

.gallery-overlay i {
    font-size: 2rem;
    color: #fff;
}

/* LIGHTBOX MODAL */
.lightbox-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.lightbox-content {
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.lightbox-image {
    max-width: 100%;
    max-height: calc(90vh - 60px);
    object-fit: contain;
    border-radius: 8px;
}

.lightbox-caption {
    margin-top: 16px;
    color: #fff;
    font-size: 1rem;
    text-align: center;
}

/* LIGHTBOX CLOSE BUTTON */
.lightbox-close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    font-size: 1.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 10000;
}

.lightbox-close:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(90deg);
}

/* LIGHTBOX NAVIGATION */
.lightbox-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    font-size: 1.8rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 10000;
}

.lightbox-prev {
    left: 20px;
}

.lightbox-next {
    right: 20px;
}

.lightbox-nav:hover {
    background: rgba(255, 174, 11, 0.8);
    border-color: #ffae0b;
    transform: translateY(-50%) scale(1.1);
}

/* LIGHTBOX COUNTER */
.lightbox-counter {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    background: rgba(0, 0, 0, 0.5);
    padding: 8px 16px;
    border-radius: 20px;
}

/* LIGHTBOX TRANSITION */
.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
    transition: opacity 0.3s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
    opacity: 0;
}
</style>
